/**
 * RolePermissions - Global context that exposes the Admin -> Role Config button permissions.
 *
 * Loads GET /role-config once when the auth token is available and exposes hasPermission() so any
 * screen can keep an action button visible but disabled when the logged-in role (Admin or User)
 * has not been granted access to it.
 *
 * Usage:
 *   const { hasPermission } = useRolePermissions();
 *   const canDownload = hasPermission(HOME_PERMISSIONS.DOWNLOAD_CSV);
 */
import React, { createContext, useState, useCallback, useEffect, useContext, useMemo } from 'react';
import axios from './AxiosConfig';
import { AppContext } from '../../RoutesWithAuth';

export const ROLE_CONFIG_URL = '/role-config';

export const NO_ACCESS_TOOLTIP = 'Your role does not have access to this action';

/**
 * Permissions configurable for the Home menu, as [menu, screen, button].
 * The names must match ROLE_CONFIG_SECTIONS in components/routes/newUI/admin/RoleConfig.js.
 */
export const HOME_PERMISSIONS = {
    DOWNLOAD_CSV: ['Home', 'Dashboard', 'Download CSV'],
    ADD_NOTE: ['Home', 'Request Detail', 'Add Note'],
    AUDIT_TRAIL: ['Home', 'Request Detail', 'Audit Trail'],
    CLONE_REQUEST: ['Home', 'Request Detail', 'Clone Request'],
    STATUS_UPDATE: ['Home', 'Request Detail', 'Status Update'],
    VIEW_REQUEST: ['Home', 'Request Detail', 'View Request'],
    JIRA_ID: ['Home', 'Request Detail', 'Jira Edit'],
    REQUEST_AUTOMATED: ['Home', 'Request Detail', 'Request Automated Edit'],
    CREATE_RECREATE: ['Home', 'DownStream Applications', 'Create/Recreate'],
    RECONSOLIDATE: ['Home', 'DownStream Applications', 'Reconsolidate'],
    EDIT_RESPONSE: ['Home', 'DownStream Applications - View Response', 'Edit Response'],
    REUPLOAD: ['Home', 'DownStream Applications - Consolidate Response', 'Reupload'],
};
export const ADMIN_PERMISSIONS = {
    APPSCOPE_B2B_DISPATCH: ['Admin', 'Apps Scope - B2B Users', 'Dispatch'],
    APPSCOPE_B2B_EDIT: ['Admin', 'Apps Scope - B2B Users', 'Edit'],
    APPSCOPE_CUSTOMER_DISPATCH: ['Admin', 'Apps Scope - Customer', 'Dispatch'],
    APPSCOPE_CUSTOMER_EDIT: ['Admin', 'Apps Scope - Customer', 'Edit'],
    APPSCOPE_DRIVER_DISPATCH: ['Admin', 'Apps Scope - Driver', 'Dispatch'],
    APPSCOPE_DRIVER_EDIT: ['Admin', 'Apps Scope - Driver', 'Edit'],
    APPSCOPE_EMPLOYEE_DISPATCH: ['Admin', 'Apps Scope - Employee', 'Dispatch'],
    APPSCOPE_EMPLOYEE_EDIT: ['Admin', 'Apps Scope - Employee', 'Edit'],
    APPSCOPE_SURVIVOR_DISPATCH: ['Admin', 'Apps Scope - Survivor', 'Dispatch'],
    APPSCOPE_SURVIVOR_EDIT: ['Admin', 'Apps Scope - Survivor', 'Edit'],
    APPSCOPE_TCI_CUSTOMER_DISPATCH: ['Admin', 'Apps Scope - TCI Customer', 'Dispatch'],
    APPSCOPE_TCI_CUSTOMER_EDIT: ['Admin', 'Apps Scope - TCI Customer', 'Edit'],
    AUTOCLOSE_B2B_EDIT: ['Admin', 'Auto Close Settings - B2B Users', 'Edit'],
    AUTOCLOSE_CUSTOMER_EDIT: ['Admin', 'Auto Close Settings - Customer', 'Edit'],
    AUTOCLOSE_EMPLOYEE_EDIT: ['Admin', 'Auto Close Settings - Employee', 'Edit'],
    AUTOCLOSE_TCI_CUSTOMER_EDIT: ['Admin', 'Auto Close Settings - TCI Customer', 'Edit'],
    CONTACTS_EDIT: ['Admin', 'Contacts', 'Edit'],
    DSAR_AUTO_CREATE: ['Admin', 'Create DSAR - Auto', 'Create'],
    DSAR_AUTO_GENERATE: ['Admin', 'Create DSAR - Auto', 'Generate'],
    DSAR_MANUAL_CREATE: ['Admin', 'Create DSAR - Manual', 'Create'],
    ESC_FREQ_DAILY_ESC: ['Admin', 'Escalation Frequency - Daily', 'Escalate Now'],
    ESC_FREQ_DAILY_SAVE: ['Admin', 'Escalation Frequency - Daily', 'Save'],
    ESC_FREQ_WEEKLY_ESC: ['Admin', 'Escalation Frequency - Weekly', 'Escalate Now'],
    ESC_FREQ_WEEKLY_SAVE: ['Admin', 'Escalation Frequency - Weekly', 'Save'],
    ESC_SETTINGS_B2B_EDIT: ['Admin', 'Escalation Settings - B2B Users', 'Edit'],
    ESC_SETTINGS_CUSTOMER_EDIT: ['Admin', 'Escalation Settings - Customer', 'Edit'],
    ESC_SETTINGS_DRIVER_EDIT: ['Admin', 'Escalation Settings - Driver', 'Edit'],
    ESC_SETTINGS_EMPLOYEE_EDIT: ['Admin', 'Escalation Settings - Employee', 'Edit'],
    ESC_SETTINGS_SURVIVOR_EDIT: ['Admin', 'Escalation Settings - Survivor', 'Edit'],
    ESC_SETTINGS_TCI_CUSTOMER_EDIT: ['Admin', 'Escalation Settings - TCI Customer', 'Edit'],
    FTP_JIRA_SETTINGS: ['Admin', 'FTP/JIRA Settings', 'Edit'],
    REQ_REVIEW_B2B_EDIT: ['Admin', 'Request Review - B2B Users', 'Edit'],
    REQ_REVIEW_CUSTOMER_EDIT: ['Admin', 'Request Review - Customer', 'Edit'],
    REQ_REVIEW_EMPLOYEE_EDIT: ['Admin', 'Request Review - Employee', 'Edit'],
    REQ_REVIEW_SURVIVOR_EDIT: ['Admin', 'Request Review - Survivor', 'Edit'],
    REQ_REVIEW_TCI_CUSTOMER_EDIT: ['Admin', 'Request Review - TCI Customer', 'Edit'],
    RES_REVIEW_B2B_EDIT: ['Admin', 'Response Review - B2B Users', 'Edit'],
    RES_REVIEW_CUSTOMER_EDIT: ['Admin', 'Response Review - Customer', 'Edit'],
    RES_REVIEW_EMPLOYEE_EDIT: ['Admin', 'Response Review - Employee', 'Edit'],
    RES_REVIEW_TCI_CUSTOMER_EDIT: ['Admin', 'Response Review - TCI Customer', 'Edit'],
    ROLE_CONFIG_SAVE: ['Admin', 'Role Config', 'Save'],
    SYSTEM_SETTINGS_DATA_ADD: ['Admin', 'System Settings - Data Dictionary', 'Add Key'],
    SYSTEM_SETTINGS_DATA_DELETE: ['Admin', 'System Settings - Data Dictionary', 'Delete'],
    SYSTEM_SETTINGS_DATA_EDIT: ['Admin', 'System Settings - Data Dictionary', 'Edit'],
    SYSTEM_SETTINGS_LITIGATION_ADD_VIN: ['Admin', 'System Settings - Litigation', 'Add VIN'],
    SYSTEM_SETTINGS_LITIGATION_DELETE_VIN: ['Admin', 'System Settings - Litigation', 'Delete'],
    SYSTEM_SETTINGS_LITIGATION_EDIT_VIN: ['Admin', 'System Settings - Litigation', 'Edit'],
    SYSTEM_SETTINGS_RETENTION_EDIT: ['Admin', 'System Settings - Retention', 'Edit'],
    SYSTEM_SETTINGS_USER_EDIT: ['Admin', 'System Settings - User', 'Edit'],
    SYSTEM_SETTINGS_USER_SYNC_USERS: ['Admin', 'System Settings - User', 'Sync Users'],
    UPSTREAMS_REQ_EDIT: ['Admin', 'Upstreams - Request', 'Edit'],
    UPSTREAMS_RES_EDIT: ['Admin', 'Upstreams - Response', 'Edit']
};

export const RESOURCE_PERMISSIONS = {
    ADD_LIMIT: ['Resources', 'Limits', 'Add Limit'],
    ADD_CERT: ['Resources', 'Cert Mgmt', 'Add Cert'],
    EDIT_CERT: ['Resources', 'Cert Mgmt', 'Edit'],
    DELETE_CERT: ['Resources', 'Cert Mgmt', 'Delete'],
    EDIT_LIMIT: ['Resources', 'Limits', 'Edit'],
    DELETE_LIMIT: ['Resources', 'Limits', 'Delete']
};

export const TRACKING_PERMISSIONS = {
    CREATE_BULK_RESPONSE: ['Tracking', 'Bulk Upload', 'Create Bulk Response'],
    REGENERATE_FILE: ['Tracking', 'Daily Feed - FTP Systems', 'Regenerate File'],
    RESEND_FILE: ['Tracking', 'Daily Feed - FTP Systems', 'Resend File'],
    INGEST: ['Tracking', 'Daily Feed - Ingest Response', 'Ingest'],
    RETRY: ['Tracking', 'Request Queue', 'Retry'],
    EDIT_REQUEST: ['Tracking', 'Request Queue - View Request', 'Edit'],
    STATUS_UPDATE: ['Tracking', 'Request Queue - View Request', 'Status Update'],
};

// Stable key used to persist and look up a permission entry.
export const permissionKey = (menu, screen, button) => `${menu}||${screen}||${button}`;

/**
 * Normalize the /role-config payload into { key: { admin, user } }.
 * Accepts either a flat array or { permissions: [...] }.
 */
export const parseRoleConfigResponse = (data) => {
    const list = Array.isArray(data) ? data : (Array.isArray(data?.permissions) ? data.permissions : []);
    const parsed = {};
    list.forEach((item) => {
        if (!item?.menu || !item?.screen || !item?.button) return;
        parsed[permissionKey(item.menu, item.screen, item.button)] = {
            admin: Boolean(item.admin_role),
            user: Boolean(item.user_role),
        };
    });
    return parsed;
};

// Default keeps every action enabled for consumers rendered outside the provider (e.g. unit tests).
export const RolePermissionsContext = createContext({
    hasPermission: () => true,
    permissionsLoaded: false,
    refreshPermissions: () => {},
});

export function RolePermissionsProvider({ children }) {
    const context = useContext(AppContext);
    const authToken = context.authToken.get;
    const isAdmin = context.isAdmin.get;
    const [permissions, setPermissions] = useState({});
    const [permissionsLoaded, setPermissionsLoaded] = useState(false);

    const fetchPermissions = useCallback(() => {
        if (!authToken) return;
        axios.get(ROLE_CONFIG_URL, {
            headers: { Authorization: authToken }
        }).then((res) => {
            setPermissions(parseRoleConfigResponse(res.data));
        }).catch((err) => {
            console.log('Error fetching role config', err);
        }).finally(() => {
            setPermissionsLoaded(true);
        });
    }, [authToken]);

    useEffect(() => {
        if (!permissionsLoaded) fetchPermissions();
    }, [fetchPermissions, permissionsLoaded]);

    // Buttons without a saved configuration stay enabled so a missing/failed config never locks the UI.
    const hasPermission = useCallback((permission) => {
        if (!permission) return true;
        const [menu, screen, button] = permission;
        const entry = permissions[permissionKey(menu, screen, button)];
        if (!entry) return true;
        return isAdmin ? entry.admin : entry.user;
    }, [permissions, isAdmin]);

    const value = useMemo(
        () => ({ hasPermission, permissionsLoaded, refreshPermissions: fetchPermissions }),
        [hasPermission, permissionsLoaded, fetchPermissions]
    );

    return (
        <RolePermissionsContext.Provider value={value}>
            {children}
        </RolePermissionsContext.Provider>
    );
}

export const useRolePermissions = () => useContext(RolePermissionsContext);
